'use client';

import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import type { DadosContador } from '@/types/auth';
import {
  getToken,
  getDadosContador,
  setAuthData,
  clearAuthData,
  parseJwtPayload,
} from '@/lib/auth';
import * as authService from '@/services/auth.service';

export interface AuthContextValue {
  user: DadosContador | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, senha: string) => Promise<void>;
  logout: (silent?: boolean) => Promise<void>;
  refreshToken: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<DadosContador | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const hasMounted = useRef(false);
  const router = useRouter();

  // Inicializar a partir do localStorage apos mount (evita hydration mismatch)
  useEffect(() => {
    const token = getToken();
    const dados = getDadosContador();
    if (token && dados) {
      setUser(dados);
    }
    setIsLoading(false);
    hasMounted.current = true;
  }, []);

  const login = useCallback(
    async (email: string, senha: string) => {
      const response = await authService.logarContador(email, senha);

      // Cenario 1: Primeiro acesso - redirecionar para redefinir senha
      // Match por prefixo (resiliente a variações de acentuação no backend)
      if (response.mensagem?.startsWith('Primeiro acesso')) {
        toast.warning('Este é o seu primeiro acesso, redefina sua senha.');
        router.push(
          `/redefinir-senha?token=${response.retorno.token}&email=${response.retorno.email}`
        );
        return;
      }

      // Cenario 2: Conta nao verificada
      if (response.mensagem?.startsWith('A conta ainda')) {
        toast.warning(response.mensagem);
        router.push(`/verificar-conta?email=${encodeURIComponent(email)}`);
        return;
      }

      // Cenario 3: Login com sucesso
      if (response.sucesso) {
        const retorno = response.retorno;
        const token = retorno.token || '';
        const refreshTokenValue = retorno.refresh_token || '';
        const expiraEmMinutos = retorno.expira_em_minutos || 1440;

        // Extrair dados do payload JWT
        let dadosContador: DadosContador = {
          id: 0,
          nome: '',
          email: '',
          cpf_cnpj: '',
          telefone: '',
          token,
          tipo: 'contador',
        };

        try {
          const payload = parseJwtPayload(token);

          dadosContador.id = payload.idUsuario
            ? parseInt(payload.idUsuario)
            : 1;
          dadosContador.cpf_cnpj =
            retorno.cpf_cnpj || payload.cpfCnpj || '';
          dadosContador.nome = retorno.nome || payload.nome || '';
          dadosContador.email = retorno.email || payload.email || '';
          dadosContador.telefone =
            retorno.telefone || payload.telefone || '';
        } catch {
          // Se falhar ao decodificar JWT, usar dados do retorno diretamente
          dadosContador = {
            id: retorno.id || 1,
            nome: retorno.nome || '',
            email: retorno.email || email,
            cpf_cnpj: retorno.cpf_cnpj || '',
            telefone: retorno.telefone || '',
            token,
            tipo: 'contador',
          };
        }

        setAuthData(token, refreshTokenValue, expiraEmMinutos, dadosContador);
        setUser(dadosContador);
        router.push('/relatorios');
        return;
      }

      // Cenario 4: Erro generico
      toast.error(response.mensagem || 'Erro ao realizar login');
    },
    [router]
  );

  const logout = useCallback(
    async (silent = false) => {
      try {
        const token = getToken();
        if (token) {
          await authService.logoutContador(token).catch(() => {
            // Ignora erro do endpoint de logout
          });
        }
      } catch {
        // Ignora erros ao chamar logout no servidor
      }

      clearAuthData();
      setUser(null);

      if (!silent) {
        router.push('/login');
      }
    },
    [router]
  );

  const refreshToken = useCallback(async () => {
    try {
      const { getRefreshToken } = await import('@/lib/auth');
      const currentRefreshToken = getRefreshToken();
      if (!currentRefreshToken) {
        await logout(true);
        return;
      }

      const response = await authService.renovarToken(currentRefreshToken);

      if (response.sucesso && response.retorno) {
        const { token, refresh_token, expira_em_minutos } = response.retorno;
        const currentUser = getDadosContador();

        if (currentUser) {
          currentUser.token = token;
          setAuthData(
            token,
            refresh_token || currentRefreshToken,
            expira_em_minutos || 1440,
            currentUser
          );
          setUser({ ...currentUser });
        }
      } else {
        await logout(true);
      }
    } catch {
      await logout(true);
    }
  }, [logout]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      login,
      logout,
      refreshToken,
    }),
    [user, isLoading, login, logout, refreshToken]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
